import { useEffect, useMemo } from 'react';

import { type Board, type Clip } from '@air/api/types';
import { useShortIdContext } from '@air/provider-short-id';

import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';
import {
  type BoardsFetcherParams,
  type ClipsFetcherParams,
  type Fetchers,
  useCollectionGalleryViewItems,
} from '~/swr-hooks/gallery/galleryView/useCollectionGalleryViewItems';
import { galleryViewDataToUpdateMediaPlayer } from '~/swr-hooks/gallery/galleryView/utils';
import {
  publicBoardsFetcher,
  publicClipsFetcher,
  publicFilesFetcher,
} from '~/swr-hooks/gallery/galleryView/utilsCollection';
import { usePublicMediaPlayerDataUpdate } from '~/swr-hooks/mediaPlayer/usePublicMediaPlayerData';
import { isPendingSubPageId } from '~/utils/pendingSubPageIds';

export type UsePublicShowcaseGalleryViewItemsParams = {
  shortId: string;
};

export const usePublicShowcaseGalleryViewItems = ({ shortId }: UsePublicShowcaseGalleryViewItemsParams) => {
  const { updatePublicMediaPlayerData } = usePublicMediaPlayerDataUpdate(shortId);
  const { objectId, presentationSettings } = useShortIdContext();
  const isExpandNestedDisabled = presentationSettings?.lockedControls?.expandNested === true;
  const { activePage } = usePublicShowcaseContext();
  // An active search re-scopes the clip/file fetches to the WHOLE showcase tree (FOUND-584) — see
  // getPublicShowcaseClipsFilters. Boards stay page-scoped: ShortUrl.Boards.list has no whole-tree
  // option.
  const { isSearchActive } = useIsSearchActive();
  /**
   * Resolved here rather than taken as a param so every listing block, the flat gallery, and the
   * blocks search total are guaranteed to fetch and cache the SAME scope. A per-caller param would
   * let one of them drift onto a different cache key, which shows up as a duplicate fetch and a
   * search total that disagrees with the lists.
   *
   * The layout contract's `activePage` is the live `/a` route's `/page/{slug}` resolution (null =
   * Home/root → undefined = root scope).
   */
  const subPageCollectionId = activePage?.pageId;
  // A `pending:` sub-page id belongs to a page that exists only in an unsaved editor draft — not
  // fetchable from any surface, so this guards on the id itself rather than on who is hosting it.
  const isPendingSubPage = !!subPageCollectionId && isPendingSubPageId(subPageCollectionId);

  const fetchers = useMemo<Fetchers<Board, Clip>>(
    () => ({
      boardsFetcher: async (params: BoardsFetcherParams) =>
        publicBoardsFetcher({
          shortId,
          cursor: params.cursor,
          sortFieldName: params.sortFieldName,
          sortDirection: params.sortDirection,
          params: params.boardsFetchParams,
          subPageCollectionId,
        }),
      clipsFetcher: async (params: ClipsFetcherParams) =>
        publicClipsFetcher({
          shortId,
          cursor: params.cursor,
          params: params.clipsFetchParams,
          sortFieldName: params.sortFieldName,
          sortDirection: params.sortDirection,
          expandNestedContent: params.expandNestedContent,
          isSearching: isSearchActive,
          subPageCollectionId,
          rootCollectionId: objectId,
        }),
      filesFetcher: async (params: ClipsFetcherParams) =>
        publicFilesFetcher({
          shortId,
          cursor: params.cursor,
          params: params.clipsFetchParams,
          sortFieldName: params.sortFieldName,
          sortDirection: params.sortDirection,
          expandNestedContent: params.expandNestedContent,
          isSearching: isSearchActive,
          subPageCollectionId,
          rootCollectionId: objectId,
        }),
    }),
    [isSearchActive, objectId, shortId, subPageCollectionId],
  );

  const itemsData = useCollectionGalleryViewItems<Board, Clip>({
    fetchers,
    enabled: !!shortId && !isPendingSubPage,
    /**
     * `subPageCollectionId` is a SEPARATE key param, spread in only on a sub-page, so the root
     * page's key stays byte-identical to what it was before sub-pages existed and each sub-page
     * gets its own cache entry.
     *
     * The literal `collectionId: true` is untouched on purpose: it is the marker
     * `invalidateCollectionGalleryItemQueries` matches on (`keyParams.collectionId === true`), so
     * that predicate keeps invalidating every public collection gallery — sub-page entries
     * included — with no change of its own.
     */
    cacheKeyParams: {
      collectionId: true,
      shortId,
      ...(subPageCollectionId ? { subPageCollectionId } : {}),
    },
    isExpandNestedDisabled,
  });

  // Update media player cache to enable arrow key navigation between assets
  useEffect(() => {
    updatePublicMediaPlayerData(galleryViewDataToUpdateMediaPlayer(itemsData));
  }, [itemsData, updatePublicMediaPlayerData]);

  return itemsData;
};
